import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { MdDelete, MdEdit, MdClose, MdSearch, MdPersonAdd, MdFilterList } from "react-icons/md";
import { useAuth } from "../../context/AuthContext";
import { Pagination } from "../../components/shared/Pagination";
import { ClearFiltersButton, TableBodySkeleton } from "@euroasiann/shared/ui";
import { useDebouncedSearch } from "@euroasiann/shared";

const API_URL = "http://localhost:3000/api/v1";
const FIXED_PORTAL = "tech";

/* ---------------- AUTH FETCH ---------------- */
const authFetch = (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem("accessToken");

  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
};

export function AssignRolesPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  
  // Initialize state from URL params
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('limit') || '10', 10));
  const [roleFilter, setRoleFilter] = useState<string>(searchParams.get('role') || "all");
  const {
    inputValue: searchQuery,
    submittedValue: submittedSearch,
    setInputValue: setSearchQuery,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch(searchParams.get('search') || "", {
    onCommit: () => setCurrentPage(1),
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editingRole, setEditingRole] = useState<string | null>(null);

  /* ⭐ Permission flags */
  const { permissions } = useAuth();

  const canView = permissions.includes("assignRolesView");
  const canAssign = permissions.includes("assignRolesAssign");
  const canUpdate = permissions.includes("assignRolesUpdate");
  const canRemove = permissions.includes("assignRolesRemove");

  /* ---------------- FETCH ROLES ---------------- */
  const rolesQuery = useQuery({
    queryKey: ["roles", FIXED_PORTAL],
    queryFn: async () => {
      const res = await authFetch(
        `${API_URL}/assign-role/roles?portalType=${FIXED_PORTAL}`
      );
      const json = await res.json();
      return json.data || [];
    },
  });

  /* ---------------- FETCH ALL USERS (for dropdown) ---------------- */
  const allUsersQuery = useQuery({
    queryKey: ["all-users", FIXED_PORTAL],
    queryFn: async () => {
      const res = await authFetch(
        `${API_URL}/assign-role/users?portalType=${FIXED_PORTAL}&page=1&limit=10000`
      );
      const json = await res.json();
      return json.data || [];
    },
  });

  /* ---------------- FETCH PAGINATED USERS (for table) ---------------- */
  const usersQuery = useQuery({
    queryKey: ["users", FIXED_PORTAL, currentPage, itemsPerPage, submittedSearch, roleFilter],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        portalType: FIXED_PORTAL,
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });
      if (submittedSearch.trim()) params.append("search", submittedSearch);
      if (roleFilter !== "all") params.append("roleFilter", roleFilter);

      const res = await authFetch(
        `${API_URL}/assign-role/users?${params.toString()}`,
        { signal }
      );
      const json = await res.json();
      return json;
    },
  });

  const roles = rolesQuery.data || [];
  const allUsers = allUsersQuery.data || [];
  const users = usersQuery.data?.data || [];
  const pagination = usersQuery.data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 };

  const roleOptions = roles.map((r: any) => ({
    label: r.name,
    value: r._id,
  }));

  const userOptions = allUsers.map((u: any) => ({
    value: u._id,
    label: `${u.firstName} ${u.lastName} (${u.email})`,
  }));

  /* ---------------- ASSIGN ROLE ---------------- */
  const assignRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: any) => {
      const res = await authFetch(
        `${API_URL}/assign-role/assign/${userId}`,
        {
          method: "PUT",
          body: JSON.stringify({ roleId }),
        }
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", FIXED_PORTAL] });
      queryClient.invalidateQueries({ queryKey: ["all-users", FIXED_PORTAL] });
      setSelectedRole(null);
      setSelectedUser(null);
    },
  });

  /* ---------------- REMOVE ROLE ---------------- */
  const removeRoleMutation = useMutation({
    mutationFn: async (userId: string) => {
      await authFetch(`${API_URL}/assign-role/assign/${userId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", FIXED_PORTAL] });
      queryClient.invalidateQueries({ queryKey: ["all-users", FIXED_PORTAL] });
    },
  });

  // Update URL params when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (submittedSearch) params.set('search', submittedSearch);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (itemsPerPage !== 10) params.set('limit', itemsPerPage.toString());
    if (roleFilter !== 'all') params.set('role', roleFilter);
    
    setSearchParams(params, { replace: true });
  }, [submittedSearch, currentPage, itemsPerPage, roleFilter, setSearchParams]);

  // Reset to page 1 when role filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter]);

  /* ⭐ BLOCK PAGE IF NO VIEW */
  if (!canView) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-[hsl(var(--muted-foreground))]">
          You do not have permission to assign roles.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-[#0088cc]/5 dark:bg-[#0088cc]/25 min-h-screen">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] dark:text-white">
            Assign Roles
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mt-1">
            Manage user roles and permissions for Tech Portal
          </p>
        </div>
      </div>

      {/* ASSIGN ROLE CARD */}
      <div className="bg-white dark:bg-[#0088cc]/18 border border-[#0088cc]/20 dark:border-[#0088cc]/20 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <MdPersonAdd className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] dark:text-white">
            Assign Role to User
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
              Select Role
            </label>
            <Select
              options={roleOptions}
              placeholder="Choose a role..."
              value={selectedRole}
              onChange={setSelectedRole}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
              Select User
            </label>
            <Select
              options={userOptions}
              placeholder="Choose a user..."
              value={selectedUser}
              onChange={setSelectedUser}
            />
          </div>

          <div className="flex items-end">
            <button
              disabled={!canAssign || !selectedRole || !selectedUser || assignRoleMutation.isPending}
              className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-[#0088cc]/25 disabled:text-[hsl(var(--muted-foreground))] disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              onClick={() => {
                if (!canAssign) return;
                assignRoleMutation.mutate({
                  userId: selectedUser.value,
                  roleId: selectedRole.value,
                });
              }}
            >
              {assignRoleMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <MdPersonAdd className="w-5 h-5" />
                  Assign Role
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* USERS TABLE CARD */}
      <div className="bg-white dark:bg-[#0088cc]/18 border border-[#0088cc]/20 dark:border-[#0088cc]/20 rounded-xl shadow-sm overflow-hidden">
        {/* FILTERS & SEARCH */}
        <div className="p-6 border-b border-[#0088cc]/20 dark:border-[#0088cc]/20">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0088cc]/65 w-5 h-5" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#0088cc]/25 dark:border-[#0088cc]/25 bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white placeholder:text-[hsl(var(--muted-foreground))] dark:placeholder:text-[#0088cc]/65 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
              />
            </div>

            <div className="flex items-center gap-2">
              <MdFilterList className="w-5 h-5 text-[#0088cc]/65" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2.5 rounded-lg border border-[#0088cc]/25 dark:border-[#0088cc]/25 bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Roles</option>
                {roles.map((r: any) => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <ClearFiltersButton
              visible={searchQuery.trim() !== '' || roleFilter !== 'all'}
              onClick={() => {
                clearSearch();
                setRoleFilter('all');
                setCurrentPage(1);
              }}
              variant="toolbar"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0088cc]/10 dark:bg-[#0088cc]/20 border-b border-[#0088cc]/20 dark:border-[#0088cc]/25">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-[#0088cc]/20 dark:divide-gray-700">
              {usersQuery.isLoading ? (
                <TableBodySkeleton rows={6} cols={4} />
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">
                      {searchQuery || roleFilter !== "all"
                        ? "No users found matching your criteria"
                        : "No users found"}
                    </p>
                  </td>
                </tr>
              ) : (
                users.map((u: any) => (
                  <tr
                    key={u._id}
                    className="hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold">
                            {u.firstName?.[0]?.toUpperCase() || "U"}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-[hsl(var(--foreground))] dark:text-white">
                          {u.firstName} {u.lastName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--foreground))] dark:text-white">
                      {u.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {u.roleName ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-400">
                          {u.roleName}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0088cc]/10 dark:bg-[#0088cc]/20 text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">
                          No role assigned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          disabled={!canUpdate}
                          onClick={() => {
                            if (!canUpdate) return;
                            setEditingUser(u);
                            setEditingRole(u.roleId);
                            setEditModalOpen(true);
                          }}
                          className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit role"
                        >
                          <MdEdit className="w-4 h-4" />
                        </button>
                        <button
                          disabled={!canRemove}
                          onClick={() => {
                            if (!canRemove) return;
                            if (confirm(`Are you sure you want to remove the role from ${u.firstName} ${u.lastName}?`)) {
                              removeRoleMutation.mutate(u._id);
                            }
                          }}
                          className="p-2 rounded-lg bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-200 dark:hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove role"
                        >
                          <MdDelete className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {pagination.total > 0 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {/* EDIT MODAL */}
      {editModalOpen && editingUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#0088cc]/18 border border-[#0088cc]/20 dark:border-[#0088cc]/20 rounded-xl w-full max-w-md shadow-2xl relative">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] dark:text-white">
                  Edit User Role
                </h2>
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/20 transition-colors"
                >
                  <MdClose className="w-5 h-5 text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                    User Name
                  </label>
                  <input
                    disabled
                    value={`${editingUser.firstName} ${editingUser.lastName}`}
                    className="w-full px-4 py-2.5 rounded-lg border border-[#0088cc]/25 dark:border-[#0088cc]/25 bg-[#0088cc]/10 dark:bg-[#0088cc]/20 text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                    Email
                  </label>
                  <input
                    disabled
                    value={editingUser.email}
                    className="w-full px-4 py-2.5 rounded-lg border border-[#0088cc]/25 dark:border-[#0088cc]/25 bg-[#0088cc]/10 dark:bg-[#0088cc]/20 text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                    Role
                  </label>
                  <Select
                    options={roleOptions}
                    value={roleOptions.find((r: any) => r.value === editingRole) || null}
                    onChange={(opt: any) => setEditingRole(opt?.value || null)}
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-[#0088cc]/25 dark:border-[#0088cc]/25 bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/40 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-[#0088cc]/25 disabled:text-[hsl(var(--muted-foreground))] disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
                    disabled={!canUpdate || !editingRole || assignRoleMutation.isPending}
                    onClick={() => {
                      if (!canUpdate || !editingUser || !editingRole) return;
                      assignRoleMutation.mutate({
                        userId: editingUser._id,
                        roleId: editingRole,
                      });
                      setEditModalOpen(false);
                    }}
                  >
                    {assignRoleMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
